import { formatPeriodNumber } from './periodLabels'
import { deriveTeamShortNames } from './teamNames'

type FlowMapRawZone = 'D' | 'M' | 'A'
type FlowMapRelativeZone = 'defense' | 'mid' | 'attack'
type FlowMapFinish = FlowMapRelativeZone | 'finish'
type FlowMapSide = 'home' | 'away'
export type FlowMapViewId = 'full' | `q${number}`
type FlowMapGainType = 'offensive' | 'midcourt' | 'defensive'

type FlowMapTeamSummary = {
	cp: number
	mid: number
	attack: number
	scoreEvents: number
	superGoals: number
	offensiveGains: number
	points: number
	connectors: {
		defense: number
		mid: number
		attack: number
	}
}

type FlowMapViewSummary = {
	home: FlowMapTeamSummary
	away: FlowMapTeamSummary
	gains: {
		home: Record<FlowMapGainType, number>
		away: Record<FlowMapGainType, number>
	}
	cpScores: {
		home: number
		away: number
	}
}

export type FlowMapParsedRow = {
	quarter: number
	cpTeamCode: string
	turnovers: FlowMapRawZone[]
	scorerCode: string | null
	points: number
	isSuper: boolean
	isDirectCpScore: boolean
	isInProgress: boolean
}

export type FlowMapParsedData = {
	rows: FlowMapParsedRow[]
	codes: string[]
	quarterNumbers: number[]
	homeCode?: string
	awayCode?: string
}

type FlowMapViewData = {
	id: FlowMapViewId
	quarter: number | null
	buttonLabel: string
	periodLabel: string
	periodMarker: string
	hasData: boolean
	score: {
		home: number
		away: number
	}
	overview: {
		entriesIntoAttack: {
			home: number
			away: number
		}
		cpScores: {
			home: number
			away: number
		}
		superGoals: {
			home: number
			away: number
		}
		offensiveGains: {
			home: number
			away: number
		}
		gains: {
			home: Record<FlowMapGainType, number>
			away: Record<FlowMapGainType, number>
		}
	}
	flow: {
		homeCp: number
		homeMid: number
		homeAttack: number
		homeGoals: number
		awayCp: number
		awayMid: number
		awayAttack: number
		awayGoals: number
		connectors: {
			homeMidToAwayMid: number
			homeAttackToAwayMid: number
			awayDefenseToHomeMid: number
			awayMidToHomeMid: number
			hiddenHomeOffensiveGains: number
			hiddenAwayOffensiveGains: number
		}
	}
	efficiency: {
		midToAttack: {
			home: FlowMapEfficiencyCell
			away: FlowMapEfficiencyCell
		}
		attackToScore: {
			home: FlowMapEfficiencyCell
			away: FlowMapEfficiencyCell
		}
	}
}

type FlowMapEfficiencyCell = {
	from: number
	to: number
	pct: number | null
}

type FlowMapScoreTableRow = {
	name: string
	code: string
	quarterScores: number[]
	total: number
}

export type FlowMapChainBadge = {
	type: 'cp' | 'zone' | 'goal' | 'super' | 'miss'
	side: FlowMapSide
	zone?: 'D' | 'M' | 'A'
}

export type FlowMapChain = {
	quarter: number
	viewId: `q${number}`
	scoreLabel: string
	cpSide: FlowMapSide
	badges: FlowMapChainBadge[]
	scorer: FlowMapSide | null
}

export type FlowMapDashboardData = {
	teams: {
		home: {
			code: string
			name: string
			shortName: string
		}
		away: {
			code: string
			name: string
			shortName: string
		}
	}
	chains: FlowMapChain[]
	cps: Array<{
		quarter: number
		viewId: `q${number}`
		scorer: FlowMapSide | null
		points: number
		isSuper: boolean
		thirdTransitions: number
		row: FlowMapParsedRow
	}>
	quarterNumbers: number[]
	scoreTable: {
		quarters: Array<{
			number: number
			buttonLabel: string
		}>
		home: FlowMapScoreTableRow
		away: FlowMapScoreTableRow
	}
	views: Record<FlowMapViewId, FlowMapViewData>
	defaultViewId: FlowMapViewId
}

type FlowMapDeriveProps = {
	parsedRows?: FlowMapParsedData
	homeName?: string | null
	awayName?: string | null
	result?: Result
	numPeriods?: number
	teamColors?: {
		home: string
		away: string
	}
}

const createTeamSummary = (): FlowMapTeamSummary => ({
	cp: 0,
	mid: 0,
	attack: 0,
	scoreEvents: 0,
	superGoals: 0,
	offensiveGains: 0,
	points: 0,
	connectors: {
		defense: 0,
		mid: 0,
		attack: 0,
	},
})

const createGainSummary = (): Record<FlowMapGainType, number> => ({
	offensive: 0,
	midcourt: 0,
	defensive: 0,
})

const createViewSummary = (): FlowMapViewSummary => ({
	home: createTeamSummary(),
	away: createTeamSummary(),
	gains: {
		home: createGainSummary(),
		away: createGainSummary(),
	},
	cpScores: {
		home: 0,
		away: 0,
	},
})

export function flowDataToParsedRows(
	flowData: FlowData,
	_homeName: string,
	_awayName: string,
): FlowMapParsedData {
	const homeCode = 'H'
	const awayCode = 'A'

	const rows = flowData.events.map((event, index) => {
		let quarter = 1
		while (quarter <= flowData.quarterBreaks.length && index >= flowData.quarterBreaks[quarter - 1]) {
			quarter += 1
		}

		const scorerCode = event.result === 'X' || event.result === 'I'
			? null
			: event.turnovers.length % 2 === 1
				? event.cpTeamCode === 'home' ? awayCode : homeCode
				: event.cpTeamCode === 'home' ? homeCode : awayCode
		const cpTeamCode = event.cpTeamCode === 'home' ? homeCode : awayCode

		return {
			quarter,
			cpTeamCode,
			turnovers: event.turnovers,
			scorerCode,
			points: scorerCode ? (event.result === 'GG' ? 2 : 1) : 0,
			isSuper: event.result === 'GG',
			isDirectCpScore: event.turnovers.length === 0 && scorerCode === cpTeamCode,
			isInProgress: event.result === 'I',
		}
	})

	return {
		rows,
		codes: [ homeCode, awayCode ],
		quarterNumbers: Array.from(new Set(rows.map(row => row.quarter))).sort((a, b) => a - b),
		homeCode,
		awayCode,
	}
}

export function deriveFlowMapDashboardData(props: FlowMapDeriveProps): FlowMapDashboardData | null {

	const parsed = props.parsedRows || null
	if (!parsed) {
		return null
	}

	const { rows, quarterNumbers, codes } = parsed
	const homeCode = parsed.homeCode || chooseHomeCode({
		codes,
		rows,
		homeName: props.homeName || undefined,
		awayName: props.awayName || undefined,
		result: props.result,
	})
	const awayCode = parsed.awayCode || codes.find(code => code !== homeCode) || codes[0]

	if (!homeCode || !awayCode || homeCode === awayCode) {
		return null
	}

	const maxQuarter = Math.max(...quarterNumbers, props.numPeriods || 0)
	const allQuarterNumbers = Array.from({ length: maxQuarter || quarterNumbers.length || 1 }, (_, index) => index + 1)

	const viewSummaries: Record<string, FlowMapViewSummary> = {
		full: createViewSummary(),
	}
	allQuarterNumbers.forEach(quarter => {
		viewSummaries[`q${quarter}`] = createViewSummary()
	})

	const cps: FlowMapDashboardData['cps'] = [ ]

	rows.forEach(row => {
		const viewIds: FlowMapViewId[] = [ 'full', `q${row.quarter}` ]
		const cpSide = codeToSide(row.cpTeamCode, homeCode)
		const scorerSide = nullableCodeToSide(row.scorerCode, homeCode)

		viewIds.forEach(viewId => {
			const view = viewSummaries[viewId]
			view[cpSide].cp += 1
			if (row.isDirectCpScore && scorerSide === cpSide) {
				view.cpScores[cpSide] += 1
			}
			if (scorerSide) {
				view[scorerSide].scoreEvents += 1
				if (row.isSuper) {
					view[scorerSide].superGoals += 1
				}
				view[scorerSide].points += row.points
			}
		})

		applyFlowMapPossessionModel({
			row,
			homeCode,
			onTurnoverGain: ({ holderSide, end }) => {
				viewIds.forEach(viewId => {
					const view = viewSummaries[viewId]
					const gain = mapOverviewGainFromTurnoverEnd(holderSide, end)
					view.gains[gain.side][gain.type] += 1
				})
			},
			onOffensiveGain: ({ holderSide }) => {
				viewIds.forEach(viewId => {
					viewSummaries[viewId][holderSide].offensiveGains += 1
				})
			},
			onSegment: ({ holderSide, start, end, turnover }) => {
				viewIds.forEach(viewId => {
					const view = viewSummaries[viewId]
					if (start !== 'attack' && end !== 'defense') {
						view[holderSide].mid += 1
					}
					const reachesAttack = start === 'attack' || end === 'attack' || end === 'finish'
					if (reachesAttack) {
						view[holderSide].attack += 1
					}
					if (turnover && end !== 'finish') {
						view[holderSide].connectors[end] += 1
					}
				})
			},
		})

		cps.push({
			quarter: row.quarter,
			viewId: `q${row.quarter}`,
			scorer: scorerSide,
			points: row.points,
			isSuper: row.isSuper,
			thirdTransitions: countThirdTransitions(row, homeCode),
			row,
		})
	})

	const cumulativeScores = {
		home: 0,
		away: 0,
	}
	const homeQuarterScores: number[] = [ ]
	const awayQuarterScores: number[] = [ ]
	const views = { } as Record<FlowMapViewId, FlowMapViewData>

	allQuarterNumbers.forEach(quarter => {
		const viewId = `q${quarter}` as const
		const summary = viewSummaries[viewId]
		const homeScore = summary.home.points
		const awayScore = summary.away.points

		cumulativeScores.home += homeScore
		cumulativeScores.away += awayScore
		homeQuarterScores.push(homeScore)
		awayQuarterScores.push(awayScore)

		views[viewId] = buildViewData({
			id: viewId,
			quarter,
			totalQuarters: allQuarterNumbers.length,
			summary,
			cumulativeScore: {
				home: cumulativeScores.home,
				away: cumulativeScores.away,
			},
			hasData: quarterNumbers.includes(quarter),
		})
	})

	views.full = buildViewData({
		id: 'full',
		quarter: null,
		totalQuarters: allQuarterNumbers.length,
		summary: viewSummaries.full,
		cumulativeScore: {
			home: cumulativeScores.home,
			away: cumulativeScores.away,
		},
		hasData: true,
	})

	const teamShortNames = deriveTeamShortNames(
		props.homeName || homeCode,
		props.awayName || awayCode,
		homeCode,
		awayCode,
	)

	return {
		teams: {
			home: {
				code: homeCode,
				name: props.homeName || homeCode,
				shortName: teamShortNames.home,
			},
			away: {
				code: awayCode,
				name: props.awayName || awayCode,
				shortName: teamShortNames.away,
			},
		},
		cps,
		quarterNumbers: allQuarterNumbers,
		scoreTable: {
			quarters: allQuarterNumbers.map(quarter => ({
				number: quarter,
				buttonLabel: formatPeriodNumber(quarter, allQuarterNumbers.length),
			})),
			home: {
				name: props.homeName || homeCode,
				code: homeCode,
				quarterScores: homeQuarterScores,
				total: cumulativeScores.home,
			},
			away: {
				name: props.awayName || awayCode,
				code: awayCode,
				quarterScores: awayQuarterScores,
				total: cumulativeScores.away,
			},
		},
		views,
		defaultViewId: 'full',
		chains: buildChains(rows, homeCode),
	}
}

const ZONE_NAMES = ['D', 'M', 'A'] as const

function rawZoneToIndex(zone: FlowMapRawZone): number {
	return zone === 'D' ? 0 : zone === 'M' ? 1 : 2
}

function countThirdTransitions(row: FlowMapParsedRow, homeCode: string): number {
	let position = 1
	let transitions = 0

	row.turnovers.forEach(turnover => {
		const targetPosition = rawZoneToIndex(turnover)
		transitions += Math.abs(targetPosition - position)
		position = targetPosition
	})

	if (row.scorerCode) {
		const scorerSide = codeToSide(row.scorerCode, homeCode)
		const goalPosition = scorerSide === 'home' ? 2 : 0
		transitions += Math.abs(goalPosition - position)
	}

	return Math.max(1, transitions)
}

function buildChains(
	rows: FlowMapParsedRow[],
	homeCode: string,
): FlowMapChain[] {
	let homeScore = 0
	let awayScore = 0

	return rows.map(row => {
		const cpSide = codeToSide(row.cpTeamCode, homeCode)
		const scoreLabel = `${homeScore}-${awayScore}`
		const badges: FlowMapChainBadge[] = [
			{ type: 'cp', side: cpSide },
		]

		let pos = 1 // MID (centre pass)
		let holderSide = cpSide

		for (const turnover of row.turnovers) {
			const targetPos = rawZoneToIndex(turnover)

			// Show the ball traveling through intermediate zones to the turnover zone
			if (pos !== targetPos) {
				const step = targetPos > pos ? 1 : -1
				for (let p = pos + step; ; p += step) {
					badges.push({ type: 'zone', side: holderSide, zone: ZONE_NAMES[p] })
					if (p === targetPos) break
				}
			}

			// Turnover: other team gains possession at this zone
			holderSide = holderSide === 'home' ? 'away' : 'home'
			badges.push({ type: 'zone', side: holderSide, zone: ZONE_NAMES[targetPos] })
			pos = targetPos
		}

		// Final segment: advance toward scoring zone and finish
		if (row.scorerCode !== null) {
			const goalPos = holderSide === 'home' ? 2 : 0
			if (pos !== goalPos) {
				const step = goalPos > pos ? 1 : -1
				for (let p = pos + step; ; p += step) {
					badges.push({ type: 'zone', side: holderSide, zone: ZONE_NAMES[p] })
					if (p === goalPos) break
				}
			}
			badges.push({ type: row.isSuper ? 'super' : 'goal', side: holderSide })
		} else if (!row.isInProgress) {
			badges.push({ type: 'miss', side: holderSide })
		}

		const scorer = row.scorerCode ? codeToSide(row.scorerCode, homeCode) : null
		if (scorer === 'home') {
			homeScore += row.points
		} else if (scorer === 'away') {
			awayScore += row.points
		}

		return {
			quarter: row.quarter,
			viewId: `q${row.quarter}` as const,
			scoreLabel,
			cpSide,
			badges,
			scorer,
		}
	})
}

function buildViewData(props: {
	id: FlowMapViewId
	quarter: number | null
	totalQuarters: number
	summary: FlowMapViewSummary
	cumulativeScore: {
		home: number
		away: number
	}
	hasData: boolean
}): FlowMapViewData {
	const { id, quarter, totalQuarters, summary, cumulativeScore, hasData } = props

	return {
		id,
		quarter,
		buttonLabel: quarter ? formatPeriodNumber(quarter, totalQuarters) : 'Full Game',
		periodLabel: quarter ? formatFlowPeriodLabel(quarter, totalQuarters) : formatFlowPeriodLabel(totalQuarters, totalQuarters),
		periodMarker: quarter ? formatPeriodNumber(quarter, totalQuarters) : 'Game',
		hasData,
		score: quarter
			? {
				home: summary.home.points,
				away: summary.away.points,
			}
			: cumulativeScore,
		overview: {
			entriesIntoAttack: {
				home: summary.home.attack,
				away: summary.away.attack,
			},
			cpScores: summary.cpScores,
			superGoals: {
				home: summary.home.superGoals,
				away: summary.away.superGoals,
			},
			offensiveGains: {
				home: summary.home.offensiveGains,
				away: summary.away.offensiveGains,
			},
			gains: summary.gains,
		},
		flow: {
			homeCp: summary.home.cp,
			homeMid: summary.home.mid,
			homeAttack: summary.home.attack,
			homeGoals: summary.home.scoreEvents,
			awayCp: summary.away.cp,
			awayMid: summary.away.mid,
			awayAttack: summary.away.attack,
			awayGoals: summary.away.scoreEvents,
			connectors: {
				homeMidToAwayMid: summary.home.connectors.mid,
				homeAttackToAwayMid: summary.home.connectors.attack,
				awayDefenseToHomeMid: summary.away.connectors.attack,
				awayMidToHomeMid: summary.away.connectors.mid,
				hiddenHomeOffensiveGains: summary.away.connectors.defense,
				hiddenAwayOffensiveGains: summary.home.connectors.defense,
			},
		},
		efficiency: {
			midToAttack: {
				home: buildEfficiencyCell(summary.home.mid, summary.home.attack),
				away: buildEfficiencyCell(summary.away.mid, summary.away.attack),
			},
			attackToScore: {
				home: buildEfficiencyCell(summary.home.attack, summary.home.scoreEvents),
				away: buildEfficiencyCell(summary.away.attack, summary.away.scoreEvents),
			},
		},
	}
}

function buildEfficiencyCell(from: number, to: number): FlowMapEfficiencyCell {
	return {
		from,
		to,
		pct: from > 0 ? Math.round((to / from) * 100) : null,
	}
}

function applyFlowMapPossessionModel(props: {
	row: FlowMapParsedRow
	homeCode: string
	onOffensiveGain?: (offensiveGain: {
		holderSide: FlowMapSide
		zone: FlowMapRawZone
	}) => void
	onTurnoverGain?: (turnoverGain: {
		holderSide: FlowMapSide
		end: FlowMapRelativeZone
	}) => void
	onSegment: (segment: {
		holderSide: FlowMapSide
		start: FlowMapRelativeZone
		end: FlowMapFinish
		turnover: boolean
	}) => void
}) {
	const { row, homeCode, onOffensiveGain, onTurnoverGain, onSegment } = props

	const cpSide = codeToSide(row.cpTeamCode, homeCode)
	let holderSide = cpSide
	let start: FlowMapRelativeZone = 'mid'

	let index = 0
	while (index < row.turnovers.length) {
		const turnover = row.turnovers[index]
		const nextTurnover = row.turnovers[index + 1]

		if (isOffensiveGainPair(turnover, nextTurnover)) {
			onTurnoverGain?.({
				holderSide,
				end: relativeZone(holderSide, turnover),
			})
			onOffensiveGain?.({
				holderSide,
				zone: turnover,
			})
			index += 2
			continue
		}

		const end = relativeZone(holderSide, turnover)
		onTurnoverGain?.({
			holderSide,
			end,
		})
		onSegment({
			holderSide,
			start,
			end,
			turnover: true,
		})
		holderSide = holderSide === 'home' ? 'away' : 'home'
		start = inverseRelativeZone(end)
		index += 1
	}

	if (!row.isInProgress) {
		onSegment({
			holderSide,
			start,
			end: row.scorerCode === null ? 'defense' : 'finish',
			turnover: false,
		})
	}
}

function chooseHomeCode(props: {
	codes: string[]
	rows: FlowMapParsedRow[]
	homeName?: string
	awayName?: string
	result?: Result
}) {
	const { codes, rows, homeName, awayName, result } = props
	const explicitHomeCode = codes.find(code => code.toUpperCase() === 'K')

	if (explicitHomeCode) {
		return explicitHomeCode
	}

	let bestCode = codes[0]
	let bestScore = Number.NEGATIVE_INFINITY

	codes.forEach(candidateHomeCode => {
		const candidateAwayCode = codes.find(code => code !== candidateHomeCode)
		if (!candidateAwayCode) {
			return
		}

		let score = zoneBiasScore(rows, candidateHomeCode) * 6
		const homeHints = buildCodeHints(homeName)
		const awayHints = buildCodeHints(awayName)
		const upperCode = candidateHomeCode.toUpperCase()

		if (homeHints.has(upperCode)) {
			score += 4
		}
		if (awayHints.has(upperCode)) {
			score -= 4
		}

		if (result?.me && result.them) {
			const quarterPoints = summarizeQuarterPoints(rows, candidateHomeCode, candidateAwayCode)
			let mismatches = 0
			let matches = 0

			quarterPoints.forEach((scores, index) => {
				const expectedHome = result.me[index]
				const expectedAway = result.them[index]
				if (expectedHome === null || expectedHome === undefined || expectedAway === null || expectedAway === undefined) {
					return
				}
				if (scores.home === expectedHome && scores.away === expectedAway) {
					matches += 1
				} else {
					mismatches += 1
				}
			})

			score += matches * 10
			score -= mismatches * 10
		}

		if (score > bestScore) {
			bestScore = score
			bestCode = candidateHomeCode
		}
	})

	return bestCode
}

function zoneBiasScore(rows: FlowMapParsedRow[], candidateHomeCode: string) {
	let bias = 0

	rows.forEach(row => {
		if (row.cpTeamCode !== candidateHomeCode) {
			return
		}
		row.turnovers.forEach(zone => {
			if (zone === 'A') {
				bias += 1
			} else if (zone === 'D') {
				bias -= 1
			}
		})
	})

	return bias
}

function summarizeQuarterPoints(rows: FlowMapParsedRow[], homeCode: string, awayCode: string) {
	const quarterTotals = new Map<number, { home: number; away: number }>()

	rows.forEach(row => {
		if (!quarterTotals.has(row.quarter)) {
			quarterTotals.set(row.quarter, {
				home: 0,
				away: 0,
			})
		}
		const scores = quarterTotals.get(row.quarter)
		if (!scores || !row.scorerCode) {
			return
		}
		if (row.scorerCode === homeCode) {
			scores.home += row.points
		} else if (row.scorerCode === awayCode) {
			scores.away += row.points
		}
	})

	let runningHome = 0
	let runningAway = 0
	return Array.from(quarterTotals.entries())
		.sort((a, b) => a[0] - b[0])
		.map(([, score]) => {
			runningHome += score.home
			runningAway += score.away
			return {
				home: runningHome,
				away: runningAway,
			}
		})
}

function buildCodeHints(name?: string) {
	const hints = new Set<string>()
	if (!name) {
		return hints
	}
	const tokens = name
		.toUpperCase()
		.replace(/[^A-Z0-9 ]+/g, ' ')
		.split(/\s+/)
		.filter(Boolean)

	if (!tokens.length) {
		return hints
	}

	const initials = tokens.map(token => token[0]).join('')
	hints.add(initials)
	hints.add(tokens[0])
	hints.add(tokens.join(''))
	return hints
}

function formatFlowPeriodLabel(quarter: number, totalQuarters: number) {
	if (quarter === totalQuarters) {
		return 'FULL TIME'
	}
	if (totalQuarters === 4 && quarter === 2) {
		return 'HALF TIME'
	}
	if (totalQuarters === 4 && quarter === 3) {
		return 'THREE-QUARTER TIME'
	}
	return formatPeriodNumber(quarter, totalQuarters)
}

function codeToSide(code: string, homeCode: string): FlowMapSide {
	return code === homeCode ? 'home' : 'away'
}

function nullableCodeToSide(code: string | null, homeCode: string): FlowMapSide | null {
	if (!code) {
		return null
	}
	return code === homeCode ? 'home' : 'away'
}

function mapOverviewGainFromTurnoverEnd(turnoverSide: FlowMapSide, end: FlowMapRelativeZone): {
	side: FlowMapSide
	type: FlowMapGainType
} {
	if (turnoverSide === 'home') {
		return {
			side: 'away' as const,
			type: end === 'defense'
				? 'offensive'
				: end === 'mid'
					? 'midcourt'
					: 'defensive',
		}
	}

	return {
		side: 'home' as const,
		type: end === 'defense'
			? 'offensive'
			: end === 'mid'
				? 'midcourt'
				: 'defensive',
	}
}

function isOffensiveGainPair(
	firstTurnover: FlowMapRawZone | undefined,
	secondTurnover: FlowMapRawZone | undefined,
) {
	return !!firstTurnover
		&& firstTurnover === secondTurnover
		&& firstTurnover !== 'M'
}

function relativeZone(side: FlowMapSide, zone: FlowMapRawZone): FlowMapRelativeZone {
	if (zone === 'M') {
		return 'mid'
	}
	if (side === 'home') {
		return zone === 'D' ? 'defense' : 'attack'
	}
	return zone === 'A' ? 'defense' : 'attack'
}

function inverseRelativeZone(zone: FlowMapFinish): FlowMapRelativeZone {
	if (zone === 'defense') {
		return 'attack'
	}
	if (zone === 'attack') {
		return 'defense'
	}
	return 'mid'
}
